import { Activity, Brain, FileText, AlertCircle, HeartPulse, Stethoscope, Users, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Hero } from '../../components/Hero';

export function Headaches() {
  return (
    <div>
      <Hero
        title="Headache Treatment"
        description="Expert diagnosis and comprehensive treatment for various types of headaches, utilizing advanced diagnostic methods and personalized treatment approaches to provide lasting relief."
        image="https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?auto=format&fit=crop&q=80"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Comprehensive Headache Care</h2>
            <p className="text-gray-600 mb-6">
              Our specialists are experienced in diagnosing and treating all types of headaches, 
              from common tension headaches to complex migraines and cluster headaches. We understand 
              how debilitating chronic headaches can be and work diligently to identify triggers 
              and develop effective treatment strategies.
            </p>
            <p className="text-gray-600">
              Using advanced diagnostic tools and evidence-based treatment approaches, we create 
              personalized treatment plans that address both immediate pain relief and long-term 
              prevention strategies.
            </p>
          </div>
          <div className="bg-gradient-cool rounded-lg p-8 shadow-glow">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Types of Headaches We Treat</h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <Brain className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Migraines</h4>
                  <p className="text-gray-600">Severe throbbing pain, often with aura, nausea, and sensitivity to light and sound</p>
                </div>
              </li>
              <li className="flex items-start">
                <Activity className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Tension Headaches</h4>
                  <p className="text-gray-600">Constant dull ache or pressure around the head, often stress-related</p>
                </div>
              </li>
              <li className="flex items-start">
                <AlertCircle className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Cluster Headaches</h4>
                  <p className="text-gray-600">Intense pain on one side, occurring in cyclical patterns</p>
                </div>
              </li>
              <li className="flex items-start">
                <HeartPulse className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Secondary Headaches</h4>
                  <p className="text-gray-600">Headaches resulting from underlying medical conditions or injuries</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Common Symptoms</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?auto=format&fit=crop&q=80"
                  alt="Pain characteristics"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Pain Characteristics</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Throbbing or pulsating</li>
                <li>• Pressure or tightness</li>
                <li>• Sharp or stabbing</li>
                <li>• One-sided or bilateral</li>
                <li>• Variable intensity</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1559757148-5c350d0d3c56?auto=format&fit=crop&q=80"
                  alt="Associated symptoms"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Associated Symptoms</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Nausea and vomiting</li>
                <li>• Light/sound sensitivity</li>
                <li>• Visual disturbances</li>
                <li>• Dizziness</li>
                <li>• Neck pain</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1494631781929-c23495644b46?auto=format&fit=crop&q=80"
                  alt="Impact on daily life"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Impact on Daily Life</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Work disruption</li>
                <li>• Sleep disturbances</li>
                <li>• Reduced concentration</li>
                <li>• Social withdrawal</li>
                <li>• Mood changes</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-gradient-healing rounded-lg p-8 mb-16 shadow-glow">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Diagnostic Approach</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Comprehensive Evaluation</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <FileText className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Detailed headache history and pattern analysis</span>
                </li>
                <li className="flex items-start">
                  <Brain className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Neurological examination</span>
                </li>
                <li className="flex items-start">
                  <Stethoscope className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Physical examination</span>
                </li>
                <li className="flex items-start">
                  <Clock className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Headache diary analysis</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced Testing</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <Brain className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Neuroimaging (MRI/CT when indicated)</span>
                </li>
                <li className="flex items-start">
                  <Activity className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Blood tests for underlying conditions</span>
                </li>
                <li className="flex items-start">
                  <Users className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                  <span>Trigger identification assessment</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Treatment Options</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&q=80"
                  alt="Medication Management"
                  className="w-full h-40 object-cover rounded-lg"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Medication Management</h3>
              <p className="text-gray-600 mb-4">Customized medication plans including:</p>
              <ul className="space-y-2 text-gray-600">
                <li>• Acute pain relief</li>
                <li>• Preventive medications</li>
                <li>• Anti-inflammatory drugs</li>
                <li>• Specialized migraine treatments</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1571019613576-2b22c76fd955?auto=format&fit=crop&q=80"
                  alt="Lifestyle Modifications"
                  className="w-full h-40 object-cover rounded-lg"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Advanced Therapies</h3>
              <p className="text-gray-600 mb-4">Specialized treatments including:</p>
              <ul className="space-y-2 text-gray-600">
                <li>• Botox injections</li>
                <li>• Nerve blocks</li>
                <li>• Trigger point injections</li>
                <li>• Neurostimulation</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
              <div className="mb-6">
                <img
                  src="https://images.unsplash.com/photo-1666214280557-f1b5022eb634?auto=format&fit=crop&q=80"
                  alt="Support Services"
                  className="w-full h-40 object-cover rounded-lg"
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Lifestyle Management</h3>
              <p className="text-gray-600 mb-4">Comprehensive approach including:</p>
              <ul className="space-y-2 text-gray-600">
                <li>• Trigger avoidance</li>
                <li>• Stress management</li>
                <li>• Sleep hygiene</li>
                <li>• Dietary modifications</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Find Relief from Headaches</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Don't let headaches control your life. Our experienced team can help you 
            identify triggers, manage symptoms, and develop effective strategies for 
            lasting relief. Contact us to start your journey toward better health.
          </p>
          <div className="flex justify-center gap-4">
            <Link
              to="/contact"
              className="bg-blue-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors"
            >
              Schedule a Consultation
            </Link>
            <Link
              to="/resources/education"
              className="bg-white text-blue-600 border border-blue-600 px-8 py-3 rounded-md font-semibold hover:bg-blue-50 transition-colors"
            >
              Learn More
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}